import React from 'react';
import { Link } from 'react-router-dom';
import { Info, ShieldAlert } from 'lucide-react';
import NotificationPageLayout, { CodeBlock } from '../components/NotificationPageLayout';
import LangTabs from '../components/LangTabs';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'setting-up-handler', label: 'Setting Up A Handler', children: [
    { id: 'sample-notification-url', label: 'Sample Notification URL' },
    { id: 'sample-handler', label: 'Sample Notification Handler' },
  ]},
  { id: 'data-returned', label: 'Data Returned' },
  { id: 'sample-xml', label: 'Sample XML Notification' },
  { id: 'playbook', label: 'Dispute-response Playbook' },
];

const NOTIFICATION_URL_SAMPLE = `http://example.com/Notification-Handler/Chargeback-Fraud-Notification/?apisignature=chargeback&data=<R>...</R>`;

const PHP_SAMPLE = `<?php
// Post Request - Chargeback Notification
if(isset($_POST["data"])) //If we have data
{
    if($_POST["apisignature"] == "chargeback") //Chargeback Notification
    {
        //Handler code for chargeback
        $strData = $_POST["data"]; //Assign the data to variable $strData
        $XMLDataArray = simplexml_load_string($strData); //Used to parse XML at ease
        //...

        /* Obtain information from response fields */
        $ResponseR1 = (string)$XMLDataArray->R1;
        $ResponseR2 = (string)$XMLDataArray->R2;
        //... etc

        //Or loop through the elements
        /* END */

        //More code...
    }
    else if($_POST["apisignature"] == "fraudtransactionnotification") //Fraud Notification
    {
        //Handler code for fraud...
    }
}
?>`;

const CSHARP_SAMPLE = `// Post Request - Chargeback Notification
[HttpPost]
public IActionResult NotificationURL(string apisignature, string data)
{
    if(data != null) //If we have data
    {
        if(apisignature == "chargeback") //Chargeback Notification
        {
            //Handler code for chargeback

            /* Parse XML from response */
            XmlDocument xDoc = new XmlDocument();
            xDoc.LoadXml(data);
            /* END */

            /* Obtain information from response fields */
            XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
            XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
            //... etc

            //Or loop through the elements
            /* END */

            //More code...
        }
        else if(apisignature == "fraudtransactionnotification") //Fraud Notification
        {
            //Handler code for fraud...
        }
    }

    return View();
}`;

const SAMPLE_XML = `<!-- Sample XML Document, contained inside the \`data=\` parameter -->
<R>
    <R1>29-10-2015</R1>
    <R2>Len Merchant</R2>
    <R3>100</R3>
    <R4>27-10-2015</R4>
    <R5>11-08-2015</R5>
    <R6>05421215223811034925862</R6>
    <R7>022691</R7>
    <R8>4837 </R8>
    <R9>081103492586</R9>
    <R10>5523********3278</R10>
    <R11>19310</R11>
    <R12>100</R12>
    <R13>EUR</R13>
    <R14>Full Chargeback</R14>
    <R15>5a017421-0a92-4141-a4e9-2fd3969307db</R15>
</R>`;

const FIELDS = [
  { name: 'R1',  desc: 'Received Date — when eCOMM received the chargeback from the Acquirer.' },
  { name: 'R2',  desc: 'Merchant Name.' },
  { name: 'R3',  desc: 'Chargeback Amount.' },
  { name: 'R4',  desc: 'Parent Transaction Date — the date of the original transaction being disputed.' },
  { name: 'R5',  desc: 'Chargeback Date — the date the Issuer lodged the chargeback.' },
  { name: 'R6',  desc: 'ARN — Acquirer Reference Number identifying the transaction at the scheme level.' },
  { name: 'R7',  desc: 'Authorisation Code — the auth code returned on the original successful authorisation.' },
  { name: 'R8',  desc: 'Chargeback Reason — scheme reason code (e.g. 4837, 4853).' },
  { name: 'R9',  desc: 'Chargeback Reference Number.' },
  { name: 'R10', desc: 'Card Number (masked).' },
  { name: 'R11', desc: 'Parent Transaction ID — eCOMM\'s internal identifier of the original transaction.' },
  { name: 'R12', desc: 'Parent Transaction Amount — the total amount of the original transaction.' },
  { name: 'R13', desc: 'Parent Transaction Currency — ISO 4217 alpha currency code.' },
  { name: 'R14', desc: 'Chargeback Partial — "Full Chargeback" or a partial indicator.' },
  { name: 'R15', desc: 'Merchant Transaction Code — your own reference for the original transaction (matches R3 on your Sale request).' },
];

const ChargebackNotification = () => (
  <NotificationPageLayout
    currentId="chargeback"
    title="Chargeback"
    titleBadge="Notification"
    subtitle="A chargeback notification POSTed to your Notification URL when a cardholder disputes a previously processed transaction — dispatched alongside fraud notifications on the same endpoint."
    toc={TOC}
    mainTestId="chargeback-notification-page"
    sidebarTestId="chargeback-notification-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        A chargeback notification is sent to your desired Notification URL containing useful information about the transaction if a chargeback occurs. Below explains the data you can expect to be received in the Notification URL — you can use this data to handle the transaction.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        You must submit a desired Notification URL to eCOMM for it to be whitelisted and enabled for use. The notification will be sent via <strong>HTTP Form Post in XML format</strong> where you should set up a handler to parse the response.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The chargeback notification can be identified by using the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">apisignature</code> — if <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">chargeback</code> is specified then the following XML document relates to a chargeback. The XML document will be transmitted through a field called <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code>.
      </p>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 mt-5">
        <Info size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          Please note that when setting up the handler, you must also cater for the{' '}
          <Link to="/technical-documentation/notifications/fraud" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">fraud</Link>{' '}
          notification as the chargeback and fraud notification will be sent to the <strong>same URL</strong>.
        </p>
      </div>
    </section>

    {/* Setting Up A Handler */}
    <section id="setting-up-handler" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Setting Up A Handler</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        It is up to you to decide what may be the best decision when deciding on how to handle the chargeback notification. The below example is intended to give you an idea on how this can be achieved.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-3">
        If the notification URL is: <strong><code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">http://example.com/Notification-Handler/Chargeback-Fraud-Notification/</code></strong>, the HTTP POST will be sent identical to the below sample URL:
      </p>

      <div id="sample-notification-url" className="scroll-mt-28">
        <CodeBlock code={NOTIFICATION_URL_SAMPLE} lang="http" testid="notification-url-code" />
      </div>

      <p className="text-gray-700 text-sm leading-relaxed mt-4">
        The XML document will be enclosed in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">data</code> parameter which must be parsed in order to pull out the information you may need to proceed.
      </p>
      <p className="text-gray-700 text-sm leading-relaxed mt-3">
        The <strong><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">apisignature</code></strong> will be <strong><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">chargeback</code></strong> in this case.
      </p>

      <div id="sample-handler" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Sample Notification Handler</h3>
        <LangTabs
          samples={{ php: PHP_SAMPLE, csharp: CSHARP_SAMPLE }}
          testidBase="handler-lang"
        />
      </div>
    </section>

    {/* Data Returned */}
    <section id="data-returned" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Returned</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The chargeback notification will POST an XML document; below will help identify what XML fields are included and what to expect.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4 font-semibold">
        A well-defined XML document response will be returned with the following information:
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="chargeback-fields-table">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-24">Field Name</th>
              <th className="text-left p-3">Description</th>
            </tr>
          </thead>
          <tbody>
            {FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79] align-top">{f.name}</td>
                <td className="p-3 align-top">{f.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Sample XML */}
    <section id="sample-xml" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sample XML Notification</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below demonstrates what is contained inside the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter of the notification.
      </p>
      <CodeBlock code={SAMPLE_XML} lang="xml" testid="chargeback-sample-code" />
    </section>

    {/* Playbook (value-add) */}
    <section id="playbook" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Dispute-response Playbook</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        An opinionated checklist that will save you money every time a chargeback lands.
      </p>
      <ul className="space-y-2 text-gray-700 text-sm leading-relaxed">
        {[
          'Alert a human on-call the moment the chargeback notification is POSTed — do not rely on email alone.',
          'Match R15 (Merchant Transaction Code) against your orders table and lock the record for dispute.',
          'Compile evidence immediately: order receipts, delivery confirmations, IP & device logs, T&Cs acceptance.',
          'For 3D Secure authenticated transactions, include the 3DS liability-shift evidence — this is often decisive.',
          'Route your representment bundle to csr@ecomm365.com before the scheme\'s respond-by date (typically 20–45 days).',
          'Log the chargeback against R15 for regulatory reporting and to monitor your chargeback-to-sales ratio.',
        ].map((t) => (
          <li key={t} className="flex items-start gap-2">
            <ShieldAlert size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4 flex items-start gap-3 mt-5">
        <Info size={18} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          <strong>Proactive refund</strong>: if a cardholder contacts you <em>before</em> filing a chargeback, issue a{' '}
          <Link to="/technical-documentation/api-reference/refund" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Refund</Link>{' '}
          directly. It is cheaper than a chargeback (no scheme fee, no impact on your chargeback ratio).
        </p>
      </div>
    </section>
  </NotificationPageLayout>
);

export default ChargebackNotification;
